import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import _ from "lodash";
import React, { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { ProcessingAttrsDetails } from "../../../interfaces/api";
import { useUpdateReview } from "../OrionQueries";
import ClassificationForm from "./ClassificationForm";
import ExtractionForm from "./ExtractionForm";
import FormActionBar from "./ResultFormActionBar";
import {
  fillMissingExtraction,
  filterClassificationQueries,
  filterExtractionQueries,
  getCompleteOutput,
  getResultFormSchema,
} from "./ResultFormUtils";

const useReviewForm = ({
  processing,
  coreIdx,
}: {
  processing: ProcessingAttrsDetails;
  coreIdx: number;
}) => {
  const config = processing.config;
  const result = processing.result!.analysis[coreIdx];
  const review = processing.review!.analysis[coreIdx];
  const schema = useMemo(() => getResultFormSchema(config), [config]);
  type ReviewForm = z.infer<typeof schema>;
  const filledOutput = useMemo(() => fillMissingExtraction(result), [result]);
  const filledReviewedOutput = fillMissingExtraction(review);

  const defaultValues = getCompleteOutput(
    processing.config,
    filledOutput,
    filledReviewedOutput
  );

  return useForm<ReviewForm>({
    defaultValues: defaultValues,
    resolver: zodResolver(schema),
  });
};

const ResultForm: React.FC<{
  processing: ProcessingAttrsDetails;
  coreIdx: number;
}> = ({ processing, coreIdx }) => {
  const { ...methods } = useReviewForm({
    processing,
    coreIdx,
  });

  const { mutate: updateReview, isPending } = useUpdateReview();

  const onSubmit = (data: any) => {
    console.log("Form values at submit:", data);

    const filteredClassificationQueries = processing.config.classification
      ? filterClassificationQueries(
          processing.config.classification.queries,
          data.classification.split
        ).map((query) => query.name)
      : [];
    const filteredExtractionQueries = processing.config.extraction
      ? filterExtractionQueries(
          processing.config.extraction.queries,
          data.classification
        ).map((query) => query.id)
      : [];
    const payload = {
      classification: data.classification
        ? _.pick(data.classification, filteredClassificationQueries)
        : null,
      extraction: data.extraction
        ? _.pick(data.extraction, filteredExtractionQueries)
        : null,
    };
    console.log("Payload", payload);
    updateReview({
      processingId: processing._id,
      review: payload,
      coreIndex: coreIdx,
    });
  };

  const onError = (data: any) => {
    console.log("Errors", data);
    console.log("Errored Form", methods.watch());
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit, onError)}
        className="h-full overflow-hidden flex flex-col"
      >
        <FormActionBar />
        <div
          className={classNames(
            "flex flex-col flex-1 overflow-auto p-4 gap-2",
            {
              "pointer-events-none opacity-50": isPending,
            }
          )}
        >
          {(!!processing.config.classification ||
            !!processing.config.split) && (
            <ClassificationForm config={processing.config} />
          )}
          <ExtractionForm config={processing.config} />
        </div>
      </form>
    </FormProvider>
  );
};

export default ResultForm;
